/**
 * Environment-aware URLs for the Libby the Lion & Friends site.
 *
 * - `PUBLIC_SITE_ORIGIN`: this SPA’s origin (links, canonical, OG URL). Dev defaults to
 *   http://localhost:3001; production defaults to https://www.libbythelion.com.
 *   Override with REACT_APP_PUBLIC_SITE_URL when needed.
 *
 * - `NEWSLETTER_API_URL`: LibbyNewsletter.Api origin. Dev defaults to http://localhost:5144;
 *   production defaults to https://api.libbythelion.com. Override with
 *   REACT_APP_NEWSLETTER_API_URL (see `.env.example`). CORS on the API must allow PUBLIC_SITE_ORIGIN.
 */
function trimTrailingSlash(url) {
  return (url || '').replace(/\/$/, '');
}

function readEnvUrl(raw, fallback) {
  if (raw == null) return fallback();
  const trimmed = String(raw).trim();
  if (!trimmed) return fallback();
  return trimTrailingSlash(trimmed);
}

function readBoolEnv(raw, fallback) {
  if (raw == null || raw === '') return fallback;
  const v = String(raw).trim().toLowerCase();
  if (v === 'true' || v === '1' || v === 'yes') return true;
  if (v === 'false' || v === '0' || v === 'no') return false;
  return fallback;
}

function inferPublicSiteOrigin() {
  return readEnvUrl(process.env.REACT_APP_PUBLIC_SITE_URL, () => {
    if (process.env.NODE_ENV === 'production') return 'https://www.libbythelion.com';
    return 'http://localhost:3001';
  });
}

function inferNewsletterApiUrl() {
  return readEnvUrl(process.env.REACT_APP_NEWSLETTER_API_URL, () => {
    if (process.env.NODE_ENV === 'production') return 'https://api.libbythelion.com';
    return 'http://localhost:5144';
  });
}

export const PUBLIC_SITE_ORIGIN = inferPublicSiteOrigin();
export const NEWSLETTER_API_URL = inferNewsletterApiUrl();

/** Same host as newsletter API (shop routes live on LibbyNewsletter.Api). */
export const API_BASE_URL = NEWSLETTER_API_URL;

/** Canonical URL for the live homepage (initial launch). */
export const SITE_HOME_URL = `${PUBLIC_SITE_ORIGIN}/`;

/** Set to true to show the Videos pillar in the Discover section. */
export const SHOW_VIDEOS = false;

/** Set to true to show Shop in the main site navigation. */
export const SHOW_SHOP = false;

export const INSTAGRAM_URL = readEnvUrl(process.env.REACT_APP_INSTAGRAM_URL, () =>
  'https://www.instagram.com/libbythelionandfriends'
);

export const TIKTOK_URL =
  process.env.REACT_APP_TIKTOK_URL?.trim() ||
  'https://www.tiktok.com/@libbythelionandfriends?lang=en';

export const YOUTUBE_URL = readEnvUrl(process.env.REACT_APP_YOUTUBE_URL, () =>
  'https://www.youtube.com/channel/UC1smAFjebvzDJM_OWI0vxkw'
);

/** Footer / CTA social links (Facebook always shows). */
export const SHOW_INSTAGRAM = readBoolEnv(process.env.REACT_APP_SHOW_INSTAGRAM, true);
export const SHOW_YOUTUBE = readBoolEnv(process.env.REACT_APP_SHOW_YOUTUBE, true);
export const SHOW_TIKTOK = readBoolEnv(process.env.REACT_APP_SHOW_TIKTOK, true);

/** Set to true to show the newsletter signup section and related nav/CTAs. */
export const SHOW_NEWSLETTER = readBoolEnv(
  process.env.REACT_APP_SHOW_NEWSLETTER,
  process.env.NODE_ENV !== 'production'
);

/** @deprecated Use SITE_HOME_URL — kept for older imports. */
export const COMING_SOON_PAGE_URL = SITE_HOME_URL;
